// /app/frontend/src/components/ui/form-stepper.jsx
import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * FormStepper - Left-side navigation for multi-step forms
 * 
 * @param {Array} steps - Array of step objects { id, label, icon?, description? }
 * @param {number} currentStep - Current active step (1-indexed)
 * @param {function} onStepClick - Callback when step is clicked (optional)
 * @param {number} completedSteps - Number of completed steps (optional)
 */
export function FormStepper({ 
  steps, 
  currentStep, 
  onStepClick,
  completedSteps = 0,
  className 
}) {
  return (
    <div className={cn("stepper-container", className)}>
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber <= completedSteps || stepNumber < currentStep;
        // Allow clicking ANY step when onStepClick is provided
        const isClickable = !!onStepClick;
        
        return (
          <React.Fragment key={step.id || stepNumber}>
            <div
              onClick={() => isClickable && onStepClick(stepNumber)}
              className={cn(
                "stepper-item",
                isActive && "stepper-item-active",
                isCompleted && !isActive && "stepper-item-completed",
                isClickable && "cursor-pointer hover:bg-muted/50 transition-colors",
                !isClickable && "cursor-default"
              )}
              data-testid={`stepper-step-${stepNumber}`}
            >
              <div className={cn(
                "stepper-number",
                isActive && "stepper-number-active"
              )}>
                {isCompleted && !isActive ? (
                  <Check className="h-4 w-4" />
                ) : (
                  stepNumber
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {step.icon && <step.icon className="h-4 w-4 flex-shrink-0" />}
                  <span className="truncate">{step.label}</span>
                </div>
                {step.description && (
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {step.description}
                  </p>
                )}
              </div>
            </div>
            {index < steps.length - 1 && (
              <div className={cn(
                "stepper-line",
                isCompleted && "stepper-line-completed"
              )} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * FormStepperHorizontal - Horizontal stepper for mobile/compact views
 */
export function FormStepperHorizontal({ 
  steps, 
  currentStep, 
  onStepClick,
  className 
}) {
  return (
    <div className={cn("flex items-center gap-2 overflow-x-auto pb-2", className)}>
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === currentStep;
        const isCompleted = stepNumber < currentStep;
        
        return (
          <React.Fragment key={step.id || stepNumber}>
            <button
              onClick={() => onStepClick && onStepClick(stepNumber)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-full text-sm whitespace-nowrap",
                "transition-all duration-200",
                isActive && "bg-teal-500 text-white font-medium",
                isCompleted && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
                !isActive && !isCompleted && "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
              )}
              data-testid={`stepper-h-step-${stepNumber}`}
            >
              <span className={cn(
                "flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold",
                isActive && "bg-white/20",
                isCompleted && "bg-emerald-500 text-white",
                !isActive && !isCompleted && "bg-slate-300 dark:bg-slate-600"
              )}>
                {isCompleted ? <Check className="h-3 w-3" /> : stepNumber}
              </span>
              <span className="hidden sm:inline">{step.label}</span>
            </button>
            {index < steps.length - 1 && (
              <div className={cn(
                "w-8 h-0.5 flex-shrink-0",
                isCompleted ? "bg-emerald-500" : "bg-slate-200 dark:bg-slate-700"
              )} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * StepperProgress - Simple progress indicator
 */
export function StepperProgress({ current, total, className }) {
  const percentage = Math.round((current / total) * 100);
  
  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Progress</span>
        <span className="font-medium">{current} of {total}</span>
      </div>
      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export default FormStepper;
